import {
    ChangeDetectionStrategy,
    Component,
    computed,
    input,
    output,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule } from '@angular/material/dialog';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { NgIcon } from '@ng-icons/core';
import { QpxButton, QpxIcon } from '../../directives';
import { LoadingIcon } from '../loading-icon';
import type {
    DialogCancelButtonInput,
    DialogCancelButtonOptions,
    DialogConfirmButtonInput,
    DialogConfirmButtonOptions,
    DialogPresentation,
    ProcessingAppearance,
} from './models';

@Component({
    selector: 'qpx-dialog-footer',
    imports: [
        MatButtonModule,
        MatDialogModule,
        NgIcon,
        MatProgressSpinnerModule,
        QpxButton,
        QpxIcon,
        LoadingIcon,
    ],
    templateUrl: './dialog-footer.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        class: 'contents',
    },
})
export class DialogFooter {
    //#region Constants
    private readonly defaultConfirmButton: DialogConfirmButtonOptions = {
        label: 'Confirm',
        icon: '',
        theme: 'primary',
    };

    private readonly defaultCancelButton: DialogCancelButtonOptions = {
        label: 'Cancel',
    };
    //#endregion

    //#region Inputs
    public readonly presentation = input<DialogPresentation>('standard');
    public readonly confirmButton = input<DialogConfirmButtonInput>('Confirm');
    public readonly cancelButton = input<DialogCancelButtonInput>('Cancel');
    public readonly confirmDisabled = input(false);
    public readonly isProcessing = input(false);
    public readonly processingAppearance = input<ProcessingAppearance>('body');
    //#endregion

    //#region State
    protected readonly resolvedConfirmButton =
        computed<DialogConfirmButtonOptions | null>(() => {
            const confirmButton = this.confirmButton();
            if (confirmButton === false) {
                return null;
            }

            if (typeof confirmButton === 'string') {
                return {
                    ...this.defaultConfirmButton,
                    label: confirmButton,
                };
            }

            return {
                ...this.defaultConfirmButton,
                ...(confirmButton ?? {}),
            };
        });

    protected readonly resolvedCancelButton =
        computed<DialogCancelButtonOptions | null>(() => {
            const cancelButton = this.cancelButton();
            if (cancelButton === false) {
                return null;
            }

            if (typeof cancelButton === 'string') {
                return {
                    ...this.defaultCancelButton,
                    icon:
                        this.presentation() === 'confirmation' ? '' : 'lucideX',
                    label: cancelButton,
                };
            }

            return {
                ...this.defaultCancelButton,
                icon: this.presentation() === 'confirmation' ? '' : 'lucideX',
                ...(cancelButton ?? {}),
            };
        });
    //#endregion

    //#region Outputs
    public readonly dismiss = output<void>();
    public readonly confirm = output<void>();
    //#endregion

    //#region Template methods
    protected onDismiss(): void {
        this.dismiss.emit();
    }

    protected onConfirm(): void {
        this.confirm.emit();
    }
    //#endregion
}
